import { useEffect, useMemo, useRef, useState } from "react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type ColumnOrderState,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Header,
} from "@tanstack/react-table";
import {
  CaretUp,
  CaretDown,
  Eye,
  DotsSixVertical,
  ChatCircle,
  CheckSquare,
  LinkSimple,
  Plus,
  X,
  ArrowsClockwise,
  CircleNotch,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Task, Sprint } from "@/types";

interface Props {
  tasks: Task[];
  selectedId: number | null;
  onRowClick: (task: Task) => void;
  // Catalogo de sprints para resolver idSprint -> nombre en la columna
  // "Sprint". El padre (TableView) ya consume useCatalogs, asi que lo
  // pasamos como prop en vez de re-fetchear aca.
  sprints?: Sprint[];
}

const DASH = "\u2014";

const fmtDate = (s: string | null | undefined) => {
  if (!s) return DASH;
  const datePart = s.split("T")[0];
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return DASH;
  return `${d}/${m}/${y}`;
};

const STORAGE_KEY_VISIBILITY = "arqui-flow-task-table-columns";
const STORAGE_KEY_ORDER = "arqui-flow-task-table-order";

function SortableHeader({ header }: { header: Header<Task, unknown> }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: header.column.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider"
    >
      <div className="flex items-center gap-1">
        <button
          {...attributes}
          {...listeners}
          className="flex h-5 w-4 cursor-grab items-center justify-center opacity-40 hover:opacity-100 active:cursor-grabbing"
          style={{ color: "#747B86" }}
          aria-label="Reordenar columna"
          onClick={(e) => e.stopPropagation()}
        >
          <DotsSixVertical size={14} weight="bold" />
        </button>

        <div
          onClick={header.column.getToggleSortingHandler()}
          className="flex flex-1 cursor-pointer items-center gap-1 select-none hover:opacity-70"
          style={{ color: "#747B86", letterSpacing: "0.4px" }}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {header.column.getIsSorted() === "asc" && (
            <CaretUp size={10} weight="bold" />
          )}
          {header.column.getIsSorted() === "desc" && (
            <CaretDown size={10} weight="bold" />
          )}
        </div>
      </div>

    </th>
  );
}

export function TaskTable({ tasks, selectedId, onRowClick, sprints = [] }: Props) {
  // Lookup id -> nombre para la columna Sprint. Memo por seguridad cuando
  // sprints viene de useCatalogs (cambia poco pero ref-stability importa).
  const sprintNameById = useMemo(() => {
    const m = new Map<number, string>();
    sprints.forEach((s) => m.set(s.idSprint, s.nombre));
    return m;
  }, [sprints]);

  const columns = useMemo<ColumnDef<Task>[]>(
    () => [
      {
        id: "nombreTarea",
        accessorKey: "nombreTarea",
        header: "Nombre",
        cell: ({ row }) => {
          const v = row.original.nombreTarea;
          const link = row.original.linkEntrega;
          return (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-medium" style={{ color: "#5D636C" }}>
                {v || DASH}
              </span>
              {link && (
                // Icon inline para abrir la entrega directo desde la tabla.
                // stopPropagation evita disparar el row click (modal).
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="Abrir link de entrega"
                  className="text-brand-green-200 hover:text-brand-green-100"
                >
                  <LinkSimple size={14} weight="bold" />
                </a>
              )}
            </span>
          );
        },
      },
      {
        id: "estado",
        accessorFn: (row) => row.nomEstado ?? "",
        header: "Estado",
        cell: ({ row }) => {
          const v = row.original.nomEstado;
          if (!v) return DASH;
          return (
            <span
              className="inline-block px-2 py-0.5 text-xs font-bold text-white"
              style={{
                backgroundColor: row.original.colorEstado ?? "#888",
                borderRadius: 4,
              }}
            >
              {v}
            </span>
          );
        },
      },
      {
        id: "prioridad",
        accessorFn: (row) => row.nomPrioridad ?? "",
        header: "Prioridad",
        cell: ({ row }) => {
          const v = row.original.nomPrioridad;
          if (!v) return DASH;
          return (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: row.original.colorPrioridad ?? "#888" }}
              />
              <span style={{ color: row.original.colorPrioridad ?? "#5D636C" }}>
                {v}
              </span>
            </span>
          );
        },
      },
      {
        id: "proyecto",
        accessorFn: (row) => row.nomProyecto ?? "",
        header: "Proyecto",
        cell: ({ getValue }) => {
          const v = getValue<string>();
          if (!v) return DASH;
          return (
            <span
              className="inline-block px-2 py-0.5 text-[11px]"
              style={{
                backgroundColor: "#EBEBEB",
                color: "#747B86",
                borderRadius: 999,
              }}
            >
              {v}
            </span>
          );
        },
      },
      {
        id: "categoria",
        accessorFn: (row) => row.nomCategoria ?? "",
        header: "Categoria",
        cell: ({ getValue }) => {
          const v = getValue<string>();
          if (!v) return DASH;
          return (
            <span
              className="inline-block px-2 py-0.5 text-[11px]"
              style={{
                backgroundColor: "#EBEBEB",
                color: "#747B86",
                borderRadius: 999,
              }}
            >
              {v}
            </span>
          );
        },
      },
      {
        id: "fechaInicio",
        accessorKey: "fechaInicio",
        header: "F. Inicio",
        cell: ({ getValue }) => (
          <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
            {fmtDate(getValue<string>())}
          </span>
        ),
      },
      {
        id: "fechaProyectada",
        accessorKey: "fechaProyectada",
        header: "F. Proyectada",
        cell: ({ getValue }) => (
          <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
            {fmtDate(getValue<string>())}
          </span>
        ),
      },
      {
        id: "diasIp",
        accessorKey: "diasIp",
        header: "Dias IP",
        cell: ({ getValue }) => {
          const v = getValue<number | null>();
          if (v == null) return DASH;
          return (
            <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
              {v}d
            </span>
          );
        },
      },
      {
        id: "porAvance",
        accessorKey: "porAvance",
        header: "Avance",
        cell: ({ getValue }) => {
          const v = getValue<number | null>();
          if (v == null) return DASH;
          const pct = Math.max(0, Math.min(100, v));
          return (
            <div className="flex items-center gap-2">
              <div
                className="relative h-1.5 w-16 overflow-hidden"
                style={{ backgroundColor: "#EBEBEB", borderRadius: 999 }}
              >
                <div
                  className="absolute left-0 top-0 h-full"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: "#ADD010",
                    borderRadius: 999,
                  }}
                />
              </div>
              <span className="font-mono text-[10px]" style={{ color: "#747B86" }}>
                {Math.round(pct)}%
              </span>
            </div>
          );
        },
      },
      {
        id: "responsable",
        accessorFn: (row) => row.nomResponsable ?? "",
        header: "Responsable",
        cell: ({ row }) => {
          const name = row.original.nomResponsable;
          if (!name) return DASH;
          const initial = name[0]?.toUpperCase() ?? "?";
          return (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="flex h-5 w-5 items-center justify-center text-[9px] font-bold text-white"
                style={{ backgroundColor: "#5D636C", borderRadius: 999 }}
              >
                {initial}
              </span>
              <span className="text-xs" style={{ color: "#5D636C" }}>
                {name}
              </span>
            </span>
          );
        },
      },
      {
        id: "caso",
        accessorFn: (row) => (row.idCaso ? `#${row.idCaso}` : ""),
        header: "Caso",
        cell: ({ getValue }) => {
          const v = getValue<string>();
          if (!v) return DASH;
          return (
            <span className="font-mono text-xs" style={{ color: "#747B86" }}>
              {v}
            </span>
          );
        },
      },
      {
        id: "commentCount",
        accessorKey: "commentCount",
        header: "Coment.",
        cell: ({ getValue }) => {
          const v = getValue<number>();
          if (!v || v === 0) return DASH;
          return (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: "#747B86" }}>
              <ChatCircle size={12} weight="bold" />
              {v}
            </span>
          );
        },
      },
      {
        id: "checklist",
        accessorFn: (row) => `${row.checklistDone ?? 0}/${row.checklistTotal ?? 0}`,
        header: "Checklist",
        cell: ({ row }) => {
          const total = row.original.checklistTotal ?? 0;
          if (total === 0) return DASH;
          const done = row.original.checklistDone ?? 0;
          return (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: "#747B86" }}>
              <CheckSquare size={12} weight="bold" />
              {done}/{total}
            </span>
          );
        },
      },
      {
        id: "fechaCompletado",
        accessorKey: "fechaCompletado",
        header: "F. Completado",
        cell: ({ getValue }) => (
          <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
            {fmtDate(getValue<string>())}
          </span>
        ),
      },
      {
        id: "fechaCreacion",
        accessorKey: "fechaCreacion",
        header: "F. Creacion",
        cell: ({ getValue }) => (
          <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
            {fmtDate(getValue<string>())}
          </span>
        ),
      },
      {
        id: "creador",
        accessorFn: (row) => row.nomCreador ?? "",
        header: "Creado por",
        cell: ({ getValue }) => (
          <span className="text-xs" style={{ color: "#5D636C" }}>
            {getValue<string>() || DASH}
          </span>
        ),
      },
      {
        id: "linkEntrega",
        accessorKey: "linkEntrega",
        header: "Link",
        cell: ({ getValue }) => {
          const v = getValue<string>();
          if (!v) return DASH;
          return (
            <a
              href={v}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs underline hover:opacity-70"
              style={{ color: "#5D636C" }}
            >
              Ver
            </a>
          );
        },
      },
      {
        id: "pesoTarea",
        accessorKey: "pesoTarea",
        header: "Peso",
        cell: ({ getValue }) => {
          const v = getValue<number | null>();
          if (v == null) return DASH;
          return (
            <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
              {v}
            </span>
          );
        },
      },
      {
        id: "horasEstimadas",
        accessorKey: "horasEstimadas",
        header: "Horas",
        cell: ({ getValue }) => {
          const v = getValue<number | null>();
          if (v == null) return DASH;
          return (
            <span className="font-mono text-xs" style={{ color: "#5D636C" }}>
              {v % 1 === 0 ? v : v.toFixed(1)}h
            </span>
          );
        },
      },
      {
        id: "sprint",
        // accessorFn devuelve el nombre del sprint para que el sort y los
        // header-filters funcionen alfabeticamente. Si no encuentra el id
        // (sprints aun no cargo) cae a string vacio.
        accessorFn: (row) => row.idSprint != null
          ? (sprintNameById.get(row.idSprint) ?? "")
          : "",
        header: "Sprint",
        cell: ({ getValue }) => {
          const v = getValue<string>();
          if (!v) return DASH;
          return (
            <span
              className="inline-block px-2 py-0.5 text-[11px] truncate max-w-[200px]"
              style={{
                backgroundColor: "#EBEBEB",
                color: "#747B86",
                borderRadius: 999,
              }}
              title={v}
            >
              {v}
            </span>
          );
        },
      },
      {
        id: "observacion",
        accessorKey: "observacion",
        header: "Observacion",
        cell: ({ getValue }) => {
          const v = getValue<string>();
          if (!v) return DASH;
          return (
            <span
              className="block max-w-[200px] truncate text-xs"
              style={{ color: "#747B86" }}
              title={v}
            >
              {v}
            </span>
          );
        },
      },
    ],
    [sprintNameById],
  );

  const defaultVisibility: VisibilityState = {
    nombreTarea: true,
    estado: true,
    prioridad: true,
    proyecto: true,
    categoria: true,
    fechaInicio: true,
    fechaProyectada: true,
    diasIp: true,
    porAvance: true,
    responsable: true,
    horasEstimadas: true,
    sprint: true,
    caso: false,
    commentCount: false,
    checklist: false,
    fechaCompletado: false,
    fechaCreacion: false,
    creador: false,
    linkEntrega: false,
    pesoTarea: false,
    observacion: false,
  };

  const defaultOrder: ColumnOrderState = columns.map((c) => c.id as string);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "fechaInicio", desc: false },
  ]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_VISIBILITY);
        return saved
          ? { ...defaultVisibility, ...JSON.parse(saved) }
          : defaultVisibility;
      } catch {
        return defaultVisibility;
      }
    },
  );

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_ORDER);
      if (!saved) return defaultOrder;
      const parsed: string[] = JSON.parse(saved);
      const fresh = defaultOrder.filter((id) => !parsed.includes(id));
      return [...parsed.filter((id) => defaultOrder.includes(id)), ...fresh];
    } catch {
      return defaultOrder;
    }
  });

  const [showColumnMenu, setShowColumnMenu] = useState(false);

  // Row selection (cambio 3): el checkbox de la fila usa esta state. Con
  // getRowId basado en idTareaArq podemos persistir la seleccion incluso
  // cuando la data refetchea (el row id es estable, no se basa en index).
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleVisibilityChange = (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
  ) => {
    setColumnVisibility((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(STORAGE_KEY_VISIBILITY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleOrderChange = (next: ColumnOrderState) => {
    setColumnOrder(next);
    try {
      localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const table = useReactTable({
    data: tasks,
    columns,
    state: { sorting, columnVisibility, columnOrder, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: handleVisibilityChange,
    onColumnOrderChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(columnOrder) : updater;
      handleOrderChange(next);
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    // Row id estable = idTareaArq. Asi la seleccion sobrevive a refetches
    // (Tanstack defaults a indice de fila, que cambia si el sort/filtros
    // mueven cosas).
    getRowId: (row) => String(row.idTareaArq),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Tareas seleccionadas para el toolbar de bulk actions.
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedIds = useMemo(
    () => selectedRows.map((r) => r.original.idTareaArq),
    [selectedRows],
  );

  const qc = useQueryClient();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showCreateSprintDialog, setShowCreateSprintDialog] = useState(false);

  async function bulkMoveToSprint(idSprint: number | null) {
    if (selectedIds.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      await api.tasks.bulkUpdateSprint(selectedIds, idSprint);
      // Tasks ya tienen la nueva asignacion; invalidamos catalogs por si el
      // sprint era recien creado (lista de sprints en el TopBar se refresca).
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["catalogs"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["capacidades"] });
      table.resetRowSelection();
    } catch (e: any) {
      alert(e?.message || "Error al mover tareas");
    } finally {
      setBulkBusy(false);
    }
  }

  // Crear sprint especial inline + mover la seleccion. Se llama desde el
  // dialogo CreateSprintInlineDialog.
  async function createSprintAndMove(nombre: string, fechaInicio: string, fechaFin: string) {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const sprint = await api.sprints.create({ nombre, fechaInicio, fechaFin });
      await api.tasks.bulkUpdateSprint(selectedIds, sprint.idSprint);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["catalogs"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["capacidades"] });
      table.resetRowSelection();
      setShowCreateSprintDialog(false);
    } catch (e: any) {
      alert(e?.message || "Error al crear sprint y mover");
    } finally {
      setBulkBusy(false);
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(active.id as string);
    const newIndex = columnOrder.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    handleOrderChange(arrayMove(columnOrder, oldIndex, newIndex));
  };

  return (
    <div
      className="flex h-full w-full flex-col bg-white"
      style={{ fontFamily: "Roboto, sans-serif" }}
    >
      <div
        className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: "#EBEBEB" }}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold" style={{ color: "#5D636C" }}>
            Tareas
          </h3>
          <span className="text-sm" style={{ color: "#747B86" }}>
            {tasks.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowColumnMenu((o) => !o)}
              className="flex items-center gap-1 border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-gray-50"
              style={{ borderColor: "#D9D9D9", borderRadius: 8, color: "#5D636C" }}
            >
              <Eye size={14} weight="bold" />
              Columnas
            </button>
            {showColumnMenu && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowColumnMenu(false)}
                />
                <div
                  className="absolute right-0 top-full z-40 mt-1 max-h-80 w-56 overflow-y-auto bg-white py-2 shadow-lg"
                  style={{ borderRadius: 8, border: "1px solid #EBEBEB" }}
                >
                  {table.getAllLeafColumns().map((column) => (
                    <label
                      key={column.id}
                      className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm transition-colors hover:bg-gray-50"
                      style={{ color: "#5D636C" }}
                    >
                      <input
                        type="checkbox"
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                      />
                      {flexRender(column.columnDef.header, {} as never)}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions toolbar: aparece cuando hay tareas seleccionadas.
          Sticky abajo del header para que quede visible al scrollear. */}
      {selectedIds.length > 0 && (
        <div
          className="flex items-center gap-3 border-b px-4 py-2"
          style={{ borderColor: "#EBEBEB", backgroundColor: "#FFFBEB" }}
        >
          <span className="text-sm font-semibold" style={{ color: "#92400E" }}>
            {selectedIds.length} seleccionada{selectedIds.length !== 1 ? "s" : ""}
          </span>
          <BulkSprintDropdown
            sprints={sprints}
            disabled={bulkBusy}
            onMove={bulkMoveToSprint}
            onCreateNew={() => setShowCreateSprintDialog(true)}
          />
          {bulkBusy && (
            <span className="flex items-center gap-1 text-xs" style={{ color: "#92400E" }}>
              <CircleNotch size={12} weight="bold" className="animate-spin" />
              Aplicando…
            </span>
          )}
          <button
            type="button"
            onClick={() => table.resetRowSelection()}
            disabled={bulkBusy}
            className="ml-auto flex items-center gap-1 text-xs font-medium disabled:opacity-50"
            style={{ color: "#92400E" }}
          >
            <X size={12} weight="bold" />
            Limpiar selección
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full text-sm">
            <thead
              className="sticky top-0 z-10 bg-white"
              style={{ borderBottom: "2px solid #EBEBEB" }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {/* Checkbox column (fixed, no drag/reorder/visibility) */}
                  <th className="px-3 py-2 w-8" style={{ borderRight: "1px solid #EBEBEB" }}>
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todas"
                      checked={table.getIsAllRowsSelected()}
                      ref={(el) => {
                        if (el) el.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected();
                      }}
                      onChange={table.getToggleAllRowsSelectedHandler()}
                      className="cursor-pointer accent-brand-green-100"
                    />
                  </th>
                  <SortableContext
                    items={headerGroup.headers.map((h) => h.column.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <SortableHeader key={header.id} header={header} />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                const isSelected = row.original.idTareaArq === selectedId;
                const isChecked = row.getIsSelected();
                return (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick(row.original)}
                    className="cursor-pointer transition-colors"
                    style={{
                      backgroundColor: isChecked ? "#FFFBEB" : (isSelected ? "#EBEBEB" : "transparent"),
                      outline: isSelected ? "3px solid #000000" : "none",
                      outlineOffset: "-3px",
                      position: "relative",
                      zIndex: isSelected ? 1 : 0,
                      borderBottom: isSelected ? "none" : "1px solid #EBEBEB",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !isChecked) e.currentTarget.style.backgroundColor = "#F8F8F8";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !isChecked) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {/* Checkbox cell (mismo motivo que el header: columna fija) */}
                    <td
                      className="px-3 py-2 w-8"
                      style={{ borderRight: "1px solid #EBEBEB" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label="Seleccionar fila"
                        checked={isChecked}
                        onChange={row.getToggleSelectedHandler()}
                        className="cursor-pointer accent-brand-green-100"
                      />
                    </td>
                    {row.getVisibleCells().map((cell, idx) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2"
                        style={{
                          color: "#5D636C",
                          position: idx === 0 ? "relative" : undefined,
                        }}
                      >
                        {idx === 0 && isSelected && (
                          <span
                            aria-hidden
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              backgroundColor: "#ADD010",
                              zIndex: 2,
                            }}
                          />
                        )}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DndContext>
        {table.getRowModel().rows.length === 0 && (
          <div
            className="flex h-32 items-center justify-center text-sm"
            style={{ color: "#747B86" }}
          >
            Sin resultados.
          </div>
        )}
      </div>

      {showCreateSprintDialog && (
        <CreateSprintInlineDialog
          taskCount={selectedIds.length}
          busy={bulkBusy}
          onCancel={() => setShowCreateSprintDialog(false)}
          onCreate={(nombre, fechaInicio, fechaFin) =>
            createSprintAndMove(nombre, fechaInicio, fechaFin)
          }
        />
      )}
    </div>
  );
}

// ============================================================
// BulkSprintDropdown - dropdown contextual del toolbar de bulk
// actions. Lista los sprints disponibles (activos) + un atajo a
// "Nuevo sprint…" inline. Click outside cierra el menu.
// ============================================================
function BulkSprintDropdown({
  sprints, disabled, onMove, onCreateNew,
}: {
  sprints: Sprint[];
  disabled: boolean;
  onMove: (idSprint: number | null) => void;
  onCreateNew: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Sprint cero al final (es el bucket "Sin Asignar"); semanales/especiales
  // primero ordenados como vienen de catalogs (sprint cero + FechaInicio DESC).
  const visibles = sprints.filter((s) => s.activo);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="flex items-center gap-1 px-3 py-1 text-xs font-semibold border rounded disabled:opacity-50"
        style={{ color: "#5D636C", borderColor: "#D9D9D9", background: "white" }}
      >
        <ArrowsClockwise size={12} weight="bold" />
        Mover a sprint…
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 min-w-[280px] max-h-72 overflow-auto bg-white shadow-lg"
          style={{ border: "1px solid #EBEBEB", borderRadius: 8 }}
        >
          {visibles.map((s) => (
            <button
              key={s.idSprint}
              type="button"
              onClick={() => { setOpen(false); onMove(s.idSprint); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-100 truncate"
              style={{ color: "#5D636C" }}
              title={s.nombre}
            >
              {s.nombre}
            </button>
          ))}
          <div style={{ borderTop: "1px solid #EBEBEB" }}>
            <button
              type="button"
              onClick={() => { setOpen(false); onCreateNew(); }}
              className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-1.5 hover:bg-stone-100 font-medium"
              style={{ color: "#5D636C" }}
            >
              <Plus size={12} weight="bold" />
              Crear nuevo sprint…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// CreateSprintInlineDialog - mini dialog para crear un sprint
// especial y mover la seleccion en un solo paso. Defaults: nombre
// "Sprint <YYYY-MM-DD>" basado en hoy + rango lunes-domingo de la
// proxima semana (heuristica razonable; el usuario puede editar).
// ============================================================
function CreateSprintInlineDialog({
  taskCount, busy, onCancel, onCreate,
}: {
  taskCount: number;
  busy: boolean;
  onCancel: () => void;
  onCreate: (nombre: string, fechaInicio: string, fechaFin: string) => void;
}) {
  // Calcular lunes y domingo de la PROXIMA semana como default razonable
  // (el caso comun es "armar un sprint para movernos a la semana siguiente").
  const today = new Date();
  const daysSinceMonday = ((today.getDay() - 1 + 7) % 7);
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() - daysSinceMonday + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const [nombre, setNombre] = useState(`Sprint ${fmt(nextMonday)}`);
  const [fechaInicio, setFechaInicio] = useState(fmt(nextMonday));
  const [fechaFin, setFechaFin] = useState(fmt(nextSunday));

  const canSubmit = nombre.trim().length > 0 && fechaInicio !== "" && fechaFin !== "" && fechaFin >= fechaInicio;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Crear sprint y mover</h3>
          <button onClick={onCancel} className="p-1 hover:bg-stone-100 rounded">
            <X size={16} weight="bold" />
          </button>
        </div>
        <p className="text-xs text-stone-500">
          Se crea un sprint especial con estas fechas y se mueven las {taskCount} tarea{taskCount !== 1 ? "s" : ""} seleccionada{taskCount !== 1 ? "s" : ""}.
        </p>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Nombre</label>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Fecha inicio</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Fecha fin</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onCreate(nombre.trim(), fechaInicio, fechaFin)}
            disabled={!canSubmit || busy}
            className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {busy ? (
              <><CircleNotch size={12} weight="bold" className="animate-spin" /> Creando…</>
            ) : (
              <><Plus size={12} weight="bold" /> Crear y mover</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
